import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi, type User } from './api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string) => Promise<{
    success: boolean;
    status?: 'waitlisted' | 'magic_link_sent';
    error?: string;
  }>;
  verifyToken: (
    token: string,
  ) => Promise<{ success: boolean; isNewUser?: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { user } = await authApi.getCurrentUser();
        setUser(user);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  // Request magic link
  const login = useCallback(async (email: string) => {
    try {
      const result = await authApi.requestMagicLink(email);
      return { success: true, status: result.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send magic link',
      };
    }
  }, []);

  // Verify magic link token
  const verifyToken = useCallback(async (token: string) => {
    try {
      const { user, isNewUser } = await authApi.verifyMagicLink(token);
      setUser(user);
      return { success: true, isNewUser };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify token',
      };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const { user } = await authApi.getCurrentUser();
      setUser(user);
    } catch {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, verifyToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
